/**
 * The "Requirement status" section of the Dashboard: the live, filterable view
 * of the requirement status report.
 *
 * The report itself is built on the server (src/best/metamodel/status_report.py)
 * and served at GET /api/report/status; the exported file stays a static page,
 * and THIS is the live view of the same numbers. Everything drawn here comes
 * from the report's JSON: the groups are the report's own (by level, by
 * subsystem, and by segment where no subsystem is stated), not regrouped in the
 * browser, and the colours are the report's meta.status_styles, not copied.
 *
 * NUMBERS AND STATUSES ONLY, the guarantee the customer report makes: the
 * request is the summary variant (no `detail`), and the internal variant is
 * deliberately not offered here. There is no requirement id, statement or
 * package anywhere on this section, and nowhere to put one.
 *
 * THE SECTION FAILS BY ITSELF. It makes its own requests and shows its own
 * loading and error states, so the tiles, coverage tables and findings around
 * it keep working when the report is unavailable.
 *
 * FILTERS live in the URL, as on the Requirements screen (`quick`, written with
 * the same helpers), so a filtered dashboard is a link somebody can paste. Level
 * and subsystem are offered; the choices come from the UNFILTERED report, so a
 * filter cannot remove its own options. A filter that matches nothing is an
 * empty report and shows the empty state, not an error.
 */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DownloadIcon from '@mui/icons-material/Download'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { api } from '../api'
import type { StatusReportFormat } from '../api'
import StatusPie from '../components/status/StatusPie'
import type { StatusReport, StatusStyle, StatusTable } from '../schema'
import { readQuick, withChoices, writeQuick } from '../components/entity/quickFilter'

/** The two fields the section can be narrowed by; also the keys of the `quick` selection. */
const LEVEL = 'level'
const SUBSYSTEM = 'subsystem'

/** The query key that turns the numbers off: the rings and their names only. */
const NUMBERS = 'numbers'

/** A request in flight, answered, or refused - for the filters it was made with. */
interface Fetched {
  key: string
  report: StatusReport | null
  failure: string
}

/**
 * The report for some filters.
 *
 * `enabled` false makes no request (the filtered view of an unfiltered
 * dashboard IS the unfiltered report, which is fetched anyway). A result that
 * arrives after the filters changed again is dropped, so a slow answer to an
 * old choice cannot overwrite the new one.
 */
function useStatusReport(levels: string[], subsystems: string[], enabled: boolean) {
  const key = JSON.stringify({ levels, subsystems })
  const [fetched, setFetched] = useState<Fetched | null>(null)

  useEffect(() => {
    if (!enabled) return
    let current = true
    api
      .statusReport(JSON.parse(key) as { levels: string[]; subsystems: string[] })
      .then((report) => current && setFetched({ key, report, failure: '' }))
      .catch((failure: unknown) => {
        const message = failure instanceof Error ? failure.message : String(failure)
        if (current) setFetched({ key, report: null, failure: message })
      })
    return () => {
      current = false
    }
  }, [key, enabled])

  const answered = enabled && fetched?.key === key ? fetched : null
  return {
    loading: enabled && answered === null,
    report: answered?.report ?? null,
    failure: answered?.failure ?? '',
  }
}

/** The distinct, non-empty rows of a table, as labels. */
function labelsOf(table: StatusTable): string[] {
  return table.rows.filter((row) => row.total > 0).map((row) => row.label)
}

/** Chosen values first-class even when the report no longer has them (a stale link). */
function withChosen(options: string[], chosen: string[]): string[] {
  return [...options, ...chosen.filter((value) => !options.includes(value))]
}

export default function RequirementStatus() {
  const [params, setParams] = useSearchParams()
  const selection = readQuick(params)
  const levels = selection[LEVEL] ?? []
  const subsystems = selection[SUBSYSTEM] ?? []
  const filtered = levels.length > 0 || subsystems.length > 0
  // On unless the link says otherwise, so an old link shows what it always did.
  const numbers = params.get(NUMBERS) !== 'off'
  const showNumbers = (on: boolean) => {
    const next = new URLSearchParams(params)
    if (on) next.delete(NUMBERS)
    else next.set(NUMBERS, 'off')
    setParams(next, { replace: true })
  }

  // The unfiltered report supplies the choices; the filtered one, the pies.
  const everything = useStatusReport([], [], true)
  const narrowed = useStatusReport(levels, subsystems, filtered)
  const shown = filtered ? narrowed : everything

  const choose = (field: string, chosen: string[]) => {
    setParams(writeQuick(params, withChoices(selection, field, chosen)), { replace: true })
  }

  const options = everything.report
  const levelOptions = withChosen(options ? labelsOf(options.summary.by_level) : [], levels)
  const subsystemOptions = withChosen(options ? labelsOf(options.summary.by_subsystem) : [], subsystems)

  return (
    <Paper sx={{ p: 2 }} component="section" aria-labelledby="requirement-status-heading">
      <Stack
        direction="row"
        useFlexGap
        sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
      >
        <Typography id="requirement-status-heading" variant="subtitle1">
          Requirement status
        </Typography>
        <ExportControl levels={levels} subsystems={subsystems} />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        The exported report shows these numbers as they are now. It has no
        change-since-last-report figures: the server holds no baseline, so those come from the
        command line (<code>best model --report</code>).
      </Typography>

      {everything.loading && <Spinner />}
      {everything.failure && (
        <Alert severity="error">
          Requirement status could not be loaded: {everything.failure}
        </Alert>
      )}

      {options && (
        <Stack spacing={2}>
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <ChoiceField
              label="Level"
              options={levelOptions}
              chosen={levels}
              onChange={(next) => choose(LEVEL, next)}
            />
            <ChoiceField
              label="Subsystem"
              options={subsystemOptions}
              chosen={subsystems}
              onChange={(next) => choose(SUBSYSTEM, next)}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={numbers}
                  onChange={(_event, on) => showNumbers(on)}
                />
              }
              label="Show numbers"
            />
          </Stack>

          {filtered && shown.loading && <Spinner />}
          {filtered && shown.failure && (
            <Alert severity="error">
              Requirement status could not be loaded: {shown.failure}
            </Alert>
          )}
          {shown.report && (
            <Pies
              report={shown.report}
              filtered={filtered}
              numbers={numbers}
              onClear={() => setParams(writeQuick(params, {}), { replace: true })}
            />
          )}
        </Stack>
      )}
    </Paper>
  )
}

function Spinner() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
      <CircularProgress size={28} aria-label="Loading requirement status" />
    </Box>
  )
}

/** One multi-select filter. Nothing to slice by (no choices) draws no control. */
function ChoiceField({
  label,
  options,
  chosen,
  onChange,
}: {
  label: string
  options: string[]
  chosen: string[]
  onChange: (next: string[]) => void
}) {
  if (options.length === 0) return null
  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      limitTags={2}
      options={options}
      value={chosen}
      onChange={(_event, next) => onChange(next)}
      renderInput={(params) => <TextField {...params} label={label} />}
      sx={{ flex: '1 1 220px', maxWidth: 360, minWidth: 200 }}
    />
  )
}

/** The status colour key: one swatch and word per style, from the report. */
function Legend({ styles }: { styles: StatusStyle[] }) {
  return (
    <Stack
      component="ul"
      direction="row"
      useFlexGap
      aria-label="Status colors used in every pie"
      sx={{ flexWrap: 'wrap', gap: '4px 16px', listStyle: 'none', p: 0, m: 0 }}
    >
      {styles.map((style) => (
        <Box
          component="li"
          key={style.label}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 14,
              height: 14,
              bgcolor: style.fill,
              border: 1,
              borderColor: 'divider',
              display: 'inline-block',
            }}
          />
          <Typography variant="caption">{style.label}</Typography>
        </Box>
      ))}
    </Stack>
  )
}

/** A heading and the small pies of one group-by; nothing at all for an empty group-by. */
function PieGroup({
  title,
  table,
  styles,
  numbers,
  empty,
}: {
  title: string
  table: StatusTable
  styles: StatusStyle[]
  numbers: boolean
  empty?: string
}) {
  const rows = table.rows.filter((row) => row.total > 0)
  return (
    <Box>
      <Typography variant="subtitle2" component="h3" gutterBottom>
        {title}
      </Typography>
      {rows.length === 0 ? (
        empty && (
          <Typography variant="body2" color="text.secondary">
            {empty}
          </Typography>
        )
      ) : (
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2 }}>
          {rows.map((row) => (
            <StatusPie
              key={row.label}
              label={row.label}
              parts={row.counts.map(({ status, count }) => ({ status, count }))}
              styles={styles}
              numbers={numbers}
            />
          ))}
        </Stack>
      )}
    </Box>
  )
}

function Pies({
  report,
  filtered,
  numbers,
  onClear,
}: {
  report: StatusReport
  filtered: boolean
  numbers: boolean
  onClear: () => void
}) {
  const styles = report.meta.status_styles
  const { by_level: byLevel, by_subsystem: bySubsystem, by_segment: bySegment } = report.summary

  if (byLevel.total === 0) {
    return (
      <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Typography variant="body2" color="text.secondary">
          {filtered ? 'No requirements match the filter.' : 'This program has no requirements.'}
        </Typography>
        {filtered && (
          <Button size="small" onClick={onClear}>
            Clear filters
          </Button>
        )}
      </Stack>
    )
  }

  return (
    <Stack spacing={2.5}>
      <Legend styles={styles} />
      <Box>
        <Typography variant="subtitle2" component="h3" gutterBottom>
          All requirements
        </Typography>
        <StatusPie
          large
          label="All requirements"
          parts={byLevel.totals.map(({ status, count }) => ({ status, count }))}
          styles={styles}
          numbers={numbers}
        />
      </Box>
      <PieGroup title="By level" table={byLevel} styles={styles} numbers={numbers} />
      <PieGroup
        title="By subsystem"
        table={bySubsystem}
        styles={styles}
        numbers={numbers}
        empty="No requirement states a subsystem."
      />
      <PieGroup
        title="By segment, where no subsystem is stated"
        table={bySegment}
        styles={styles}
        numbers={numbers}
        empty="No requirement states a segment without a subsystem."
      />
    </Stack>
  )
}

/**
 * "Export summary report" and the other formats beside it.
 *
 * The files are the report for the CURRENT filters, saved under the server's
 * name. They are fetched with code, not followed as links, so the project
 * header and the sign-in go with the request (api.downloadStatusReport). The
 * summary variant only: there is no internal item.
 */
function ExportControl({ levels, subsystems }: { levels: string[]; subsystems: string[] }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const save = (format: StatusReportFormat) => {
    setAnchor(null)
    setBusy(true)
    setFailure('')
    api
      .downloadStatusReport(format, { levels, subsystems })
      .catch((problem: unknown) =>
        setFailure(problem instanceof Error ? problem.message : String(problem)),
      )
      .finally(() => setBusy(false))
  }

  return (
    <Box sx={{ textAlign: 'right' }}>
      <ButtonGroup variant="outlined" size="small" disabled={busy}>
        <Button startIcon={<DownloadIcon />} onClick={() => save('html')}>
          Export summary report
        </Button>
        <Button
          aria-label="More export formats"
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => save('csv')}>
          <ListItemText primary="Numbers (CSV)" />
        </MenuItem>
        <MenuItem onClick={() => save('json')}>
          <ListItemText primary="Numbers (JSON)" />
        </MenuItem>
      </Menu>
      {failure && (
        <Typography variant="caption" color="error" role="alert" sx={{ display: 'block' }}>
          Export failed: {failure}
        </Typography>
      )}
    </Box>
  )
}
